import { P2GBox, P2GDivider, P2GDropdown, P2GGrid, P2GLink, P2GLinkGroup } from '@parcel2go/core-ui';
import { GenerateNumberOptions, GenerateNumbers, onlyUnique } from '../../../lib/helpers/ArrayHelper';
import { createPageLinkOptions } from '../../../lib/helpers/PaginationHelper';
import { Fragment } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export interface props {
  loading: boolean;
  skip: number;
  take: number;
  period: string;
  itemCount: number;
  totalPages?: number;
  onBack: () => void;
  onNext: () => void;
  goToPage: (page: number) => void;
  onPageSizeChanhe: (size: number) => void;
  isXs: boolean;
}

export default function ListyPaging(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const backDisabled = props.loading || props.skip === 0;
  const nextDisabled = props.loading || props.itemCount < props.take;
  const pageNumber = props.skip / props.take + 1;
  const totalPageCountIsKnown = props.totalPages != undefined && props.totalPages != null && !isNaN(props.totalPages);

  const options = useMemo(() => {
    const output = [5, 15, 25, 50, 75, 100];

    if (props.take && !output.includes(props.take)) {
      output.unshift(props.take);
    }

    return output.filter(onlyUnique);
  }, [props.take]);

  function renderLinks(currentPage: number) {
    const links = GenerateNumbers(currentPage - 1, nextDisabled ? currentPage : currentPage + 1)
      .filter((x) => x > 1)
      .filter(onlyUnique)
      .map((x) => (
        <P2GLink key={x} underline={currentPage != x ? 'always' : 'none'} onClick={() => props.goToPage(x)}>
          {x}
        </P2GLink>
      ));
    return (
      <P2GLinkGroup
        hideDivider
        links={[
          !backDisabled && <P2GLink onClick={props.onBack}>{'<<'}</P2GLink>,
          <P2GLink underline={currentPage != 1 ? 'always' : 'none'} onClick={() => props.goToPage(1)}>
            1
          </P2GLink>,
          currentPage > 3 && ('...' as any),
          ...(links as any),
          '...' as any,
          !nextDisabled && ((<P2GLink onClick={props.onNext}>{'>>'}</P2GLink>) as any)
        ]}
      />
    );
  }

  const goToPage = (currentPage: number, newPage: number) => {
    if (currentPage != newPage) {
      props.goToPage(newPage);
    }
  };

  const renderKnownPages = (currentPage: number, totalPages: number) => {
    const links = createPageLinkOptions(currentPage, totalPages).map((page, i) =>
      page.type == 'break' ? (
        <Fragment key={`break-${i}`}>...</Fragment>
      ) : (
        <P2GLink
          key={`${page.value}_${i}`}
          underline={page.selected ? 'none' : 'always'}
          onClick={() => goToPage(currentPage, page.value)}
        >
          {page.selected ? <strong>{page.value}</strong> : page.value}
        </P2GLink>
      )
    );
    return <P2GLinkGroup hideDivider links={links} />;
  };

  return (
    <P2GBox>
      <P2GBox pb={3}>
        <P2GDivider />
      </P2GBox>
      <P2GGrid container spacing={2} alignItems="center" alignContent="center">
        <P2GGrid item xs={6} sm={2} flexShrink={1}>
          <P2GDropdown
            disabled={props.loading}
            onChange={(v: string) => props.onPageSizeChanhe(parseInt(v))}
            id="Items per page"
            label={t('PAGINATION_ITEMS_PER_PAGE_LABEL', { defaultValue: 'Items per page' })}
            value={String(props.take)}
            options={options.map((x) => ({ value: x }))}
          />
        </P2GGrid>
        {totalPageCountIsKnown && props.totalPages > 1 && props.totalPages < 500 && (
          <P2GGrid item xs={6} sm={2} flexShrink={1}>
            <P2GDropdown
              disabled={props.loading}
              onChange={(v) => goToPage(pageNumber, Number(v))}
              id="Page"
              label={t('PAGINATION_PAGE_NUMBER_LABEL', { defaultValue: 'Page' })}
              value={String(pageNumber)}
              options={GenerateNumberOptions(1, props.totalPages)}
            />
          </P2GGrid>
        )}
        {(!totalPageCountIsKnown || !props.isXs) && (
          <>
            <P2GGrid item flexGrow={1}></P2GGrid>
            <P2GGrid item flexShrink={1}>
              {totalPageCountIsKnown ? renderKnownPages(pageNumber, props.totalPages) : renderLinks(pageNumber)}
            </P2GGrid>
          </>
        )}
      </P2GGrid>
    </P2GBox>
  );
}
